import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  extractSessionFromUrl,
  setSessionToken,
  setSessionId,
  getSessionToken,
  getSessionId,
} from "@/lib/utils";
import { BrandThemeProvider } from "@/lib/brand-theme";
import { OrderPage } from "@/pages/OrderPage";
import { PaymentPage } from "@/pages/PaymentPage";
import { SuccessPage } from "@/pages/SuccessPage";
import { FailedPage } from "@/pages/FailedPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function InvalidSessionUI() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Session</h1>
        <p className="text-gray-600 text-sm">
          This checkout page requires a valid session. Please access this page through the link
          provided to you.
        </p>
      </div>
    </div>
  );
}

function SessionGate({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    const sessionData = extractSessionFromUrl();

    if (sessionData) {
      // Store session_token if present
      if (sessionData.token) {
        setSessionToken(sessionData.token);
      }

      // Store session_id if present
      if (sessionData.sessionId) {
        setSessionId(sessionData.sessionId);
      }

      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete("session");
      url.searchParams.delete("session_token");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    }

    // Check if we have session data (either token or sessionId)
    const token = getSessionToken();
    const sessionId = getSessionId();
    setHasSession(!!(token || sessionId));
    setIsReady(true);
  }, []);

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!hasSession) {
    return <InvalidSessionUI />;
  }

  return <>{children}</>;
}

function SessionIdSync({ children }: { children: React.ReactNode }) {
  const { sessionId } = useParams<{ sessionId: string }>();

  useEffect(() => {
    if (sessionId) {
      const storedSessionId = getSessionId();
      if (storedSessionId !== sessionId) {
        setSessionId(sessionId);
      }
    }
  }, [sessionId]);

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionGate>
          <BrandThemeProvider>
            <Routes>
              <Route
                path="/order/:sessionId"
                element={
                  <SessionIdSync>
                    <OrderPage />
                  </SessionIdSync>
                }
              />

              <Route
                path="/order/:sessionId/pay"
                element={
                  <SessionIdSync>
                    <PaymentPage />
                  </SessionIdSync>
                }
              />

              <Route
                path="/order/:sessionId/success"
                element={
                  <SessionIdSync>
                    <SuccessPage />
                  </SessionIdSync>
                }
              />

              <Route
                path="/order/:sessionId/failed"
                element={
                  <SessionIdSync>
                    <FailedPage />
                  </SessionIdSync>
                }
              />

              <Route path="/cart" element={<RedirectToOrder />} />
              <Route path="/checkout/*" element={<RedirectToOrder />} />

              <Route path="*" element={<RedirectToOrder />} />
            </Routes>
          </BrandThemeProvider>
        </SessionGate>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function RedirectToOrder() {
  const sessionId = getSessionId();
  if (sessionId) {
    return <Navigate to={`/order/${sessionId}`} replace />;
  }
  return <InvalidSessionUI />;
}

export default App;
